import * as jose from 'jose'
import { validateApplianceCodecCapacity } from './applianceCodecLicense'
import {
    ApplianceLicenseBundleLicenses,
    ApplianceLicenseBundlePayload,
    ApplianceLicenseEntitlement,
    ApplianceLicenseFamily,
    ApplianceLicenseStatus,
    ApplianceCodecCapacity,
    ApplianceVariant,
} from './api/v1/types'

/**
 * The entitlement metric each appliance license family carries. Shared by the signer (billing
 * server) and every verifier (cloud backend and the air-gapped appliance) so the structural rules
 * cannot drift between issuing and enforcement. The sellable products (which values are offered,
 * under which names) are not fixed here — they live in the billing server's
 * appliance_license_product table and are managed by super users, so the commercial catalog can
 * change without redeploying appliances.
 */
export const applianceLicenseMetricByFamily = {
    edge_os: 'featureFlag',
    encode_decode: 'codecCapacity',
    hevc_422: 'featureFlag',
    asi: 'featureFlag',
    standalone: 'featureFlag',
} as const

const APPLIANCE_LICENSE_FAMILIES = Object.keys(applianceLicenseMetricByFamily) as ApplianceLicenseFamily[]

/** The appliance models a license may be restricted to. */
export const licensableApplianceModels: ApplianceVariant[] = [
    ApplianceVariant.nimbra410b,
    ApplianceVariant.nimbra414b,
    ApplianceVariant.nimbra520,
]

/**
 * Shape of a sellable product code, shared by the billing server and its UI. The hyphen is escaped
 * so the same source works as an HTML `pattern` attribute, which browsers compile with the `v` flag
 * — an unescaped one is a syntax error there and makes the browser drop the constraint silently.
 */
export const applianceLicenseProductCodePattern = '[A-Z][A-Z0-9_\\-]{0,63}'

/**
 * The models a serial can still be, from its imported hardware model and the model restrictions of
 * the licenses it already holds. A serial is one physical appliance, so every restricted license on
 * it must admit the same model. Undefined when nothing restricts the serial.
 */
export function allowedApplianceModels(
    knownModel: ApplianceVariant | undefined,
    allocatedProducts: Array<{ compatibleModels?: ApplianceVariant[] }>
): ApplianceVariant[] | undefined {
    let allowed = knownModel ? [knownModel] : undefined
    for (const { compatibleModels } of allocatedProducts) {
        if (!compatibleModels) continue
        allowed = allowed ? allowed.filter((model) => compatibleModels.includes(model)) : [...compatibleModels]
    }
    return allowed
}

/** Whether a product fits a serial whose models are narrowed to `allowedModels` (see allowedApplianceModels). */
export function isApplianceLicenseProductCompatible(
    product: { compatibleModels?: ApplianceVariant[] },
    allowedModels: ApplianceVariant[] | undefined
): boolean {
    if (!product.compatibleModels || !allowedModels) return true
    return product.compatibleModels.some((model) => allowedModels.includes(model))
}

/** The product fields the allocation rules read. A bundle lists the licenses it applies under `components`. */
export interface ApplianceLicenseProductRights {
    code: string
    licenseFamily: string
    entitlementMetric: string
    compatibleModels?: ApplianceVariant[]
    components?: Array<{ productCode: string; quantity: number }>
}

/**
 * The licenses allocating `product` puts on a serial: the product itself, or for a bundle each
 * component repeated by its quantity. A component missing from `productsByCode` grants nothing.
 */
export function grantedApplianceLicenseProducts<T extends ApplianceLicenseProductRights>(
    product: T,
    productsByCode: ReadonlyMap<string, T>
): T[] {
    if (!product.components) return [product]
    return product.components.flatMap(({ productCode, quantity }) => {
        const component = productsByCode.get(productCode)
        return component ? Array.from({ length: quantity }, () => component) : []
    })
}

/** Names a bundle's components the way audit entries and license lists show them. */
export function formatApplianceLicenseComponents(
    components: Array<{ productCode: string; quantity: number }>,
    nameOf: (productCode: string) => string = (productCode) => productCode
): string[] {
    return components.map(({ productCode, quantity }) =>
        quantity === 1 ? nameOf(productCode) : `${quantity} × ${nameOf(productCode)}`
    )
}

/**
 * Whether a license already on a serial rules out adding `candidate` beside it. Distinct codec products
 * stack because their pools add up, but the same codec product does not; feature flags are one per
 * family; Media Flow ten-packs stack and the Max tier may be added on top of them as the upgrade path
 * (allocations cannot be revoked), while nothing can be added once the serial holds Max. Every
 * allocation path — Billing Manager, the customer console and a bundle's own components — applies
 * this one rule to the granted licenses, never to a bundle as a whole.
 */
export function applianceLicensesConflict(
    existing: ApplianceLicenseProductRights,
    candidate: ApplianceLicenseProductRights
): boolean {
    if (candidate.licenseFamily === 'encode_decode') return existing.code === candidate.code
    if (candidate.licenseFamily === 'flow') {
        return existing.licenseFamily === 'flow' && existing.entitlementMetric === 'unlimitedFlows'
    }
    return existing.licenseFamily === candidate.licenseFamily
}

/** Aggregates allocated products without widening one codec pool's permissions using another pool. */
export function buildApplianceBundleLicenses(
    products: Array<{
        licenseFamily: ApplianceLicenseFamily
        entitlementMetric: ApplianceLicenseEntitlement['entitlementMetric']
        entitlementValue: number
        codecCapacity?: ApplianceCodecCapacity
        compatibleModels?: ApplianceVariant[]
    }>
): ApplianceLicenseBundleLicenses {
    const licenses: ApplianceLicenseBundleLicenses = {}
    for (const product of products) {
        validateApplianceLicenseEntitlement(product.licenseFamily, {
            entitlementMetric: product.entitlementMetric,
            entitlementValue: product.entitlementValue,
            ...(product.codecCapacity ? { codecPools: [product.codecCapacity] } : {}),
            compatibleModels: product.compatibleModels,
        })
        const previous = licenses[product.licenseFamily]
        let entitlement: ApplianceLicenseEntitlement
        if (product.licenseFamily === 'encode_decode') {
            const pools = [...(previous?.codecPools ?? []), ...(product.codecCapacity ? [product.codecCapacity] : [])]
            entitlement = {
                entitlementMetric: 'codecCapacity',
                entitlementValue: (previous?.entitlementValue ?? 0) + product.entitlementValue,
                codecPools: pools,
            }
        } else {
            entitlement = {
                entitlementMetric: 'featureFlag',
                entitlementValue: Math.max(previous?.entitlementValue ?? 0, product.entitlementValue),
            }
        }
        const models =
            previous?.compatibleModels && product.compatibleModels
                ? previous.compatibleModels.filter((model) => product.compatibleModels!.includes(model))
                : (previous?.compatibleModels ?? product.compatibleModels)
        if (models) entitlement.compatibleModels = models
        validateApplianceLicenseEntitlement(product.licenseFamily, entitlement)
        licenses[product.licenseFamily] = entitlement
    }
    return licenses
}

/** Throws if a single family's entitlement is not structurally valid for that family. */
export function validateApplianceLicenseEntitlement(
    family: ApplianceLicenseFamily,
    entitlement: ApplianceLicenseEntitlement
): void {
    const expectedMetric = applianceLicenseMetricByFamily[family]
    if (!expectedMetric) {
        throw new Error(`Invalid licenseFamily: ${family}`)
    }
    if (entitlement.entitlementMetric !== expectedMetric) {
        throw new Error(
            `Invalid entitlementMetric for ${family}: ${entitlement.entitlementMetric}. Expected ${expectedMetric}`
        )
    }
    if (!Number.isSafeInteger(entitlement.entitlementValue) || entitlement.entitlementValue < 1) {
        throw new Error(`Invalid entitlementValue for ${family}: ${entitlement.entitlementValue}`)
    }
    if (entitlement.entitlementMetric === 'featureFlag' && entitlement.entitlementValue !== 1) {
        throw new Error(`Invalid entitlementValue for feature flag license: ${entitlement.entitlementValue}`)
    }
    if (family === 'encode_decode') {
        if (!Array.isArray(entitlement.codecPools) || entitlement.codecPools.length === 0) {
            throw new Error('Detailed codec licenses require capacity pools')
        }
        for (const pool of entitlement.codecPools) validateApplianceCodecCapacity(pool)
        if (entitlement.codecPools.reduce((sum, pool) => sum + pool.hdChannels, 0) !== entitlement.entitlementValue) {
            throw new Error('Codec capacity total does not match its pools')
        }
    } else if (entitlement.codecPools !== undefined) {
        throw new Error('Codec pools require the codecCapacity metric')
    }
    if (
        entitlement.compatibleModels !== undefined &&
        (!Array.isArray(entitlement.compatibleModels) ||
            entitlement.compatibleModels.length === 0 ||
            entitlement.compatibleModels.some((model) => !licensableApplianceModels.includes(model)))
    )
        throw new Error('Invalid appliance license model restriction')
}

/** Throws if the bundle payload is not structurally valid (serial, version, and every family entry). */
export function validateApplianceLicenseBundlePayload(payload: ApplianceLicenseBundlePayload): void {
    if (!payload.serialNumber) {
        throw new Error('Missing serialNumber')
    }
    if (!Number.isInteger(payload.version) || payload.version < 1) {
        throw new Error(`Invalid bundle version: ${payload.version}`)
    }
    if (!payload.licenses || typeof payload.licenses !== 'object') {
        throw new Error('Bundle has no licenses')
    }
    for (const family of Object.keys(payload.licenses) as ApplianceLicenseFamily[]) {
        if (!APPLIANCE_LICENSE_FAMILIES.includes(family)) {
            throw new Error(`Unknown license family in bundle: ${family}`)
        }
        const entitlement = payload.licenses[family]
        if (entitlement) {
            validateApplianceLicenseEntitlement(family, entitlement)
        }
    }
}

/** Imports a base64-encoded ES256 public JWK used to verify appliance license tokens. */
export async function parseApplianceLicenseVerificationKey(keyBase64: string): Promise<jose.CryptoKey | Uint8Array> {
    return jose.importJWK(JSON.parse(atob(keyBase64)), 'ES256')
}

/**
 * Verifies an appliance license bundle JWT against the embedded public key, asserts it was issued for
 * the given appliance serial, and validates the payload. Returns the bundle payload on success,
 * throws otherwise. Has no backend dependencies so it can run on the appliance (edge-control) as well
 * as in the cloud.
 */
export async function verifyApplianceLicenseBundle(
    token: string,
    publicKeyBase64: string,
    expectedSerial: string
): Promise<ApplianceLicenseBundlePayload> {
    if (!token) {
        throw new Error('No license token provided')
    }
    if (!publicKeyBase64) {
        throw new Error('No license verification key provided')
    }
    const key = await parseApplianceLicenseVerificationKey(publicKeyBase64)
    const result = await jose.jwtVerify<{ applianceLicense: ApplianceLicenseBundlePayload }>(token, key, {
        algorithms: ['ES256'],
    })
    const payload = result.payload.applianceLicense
    if (!payload) {
        throw new Error('Token does not contain an applianceLicense claim')
    }
    if (payload.serialNumber !== expectedSerial) {
        throw new Error(`Serial number mismatch: token is for ${payload.serialNumber}, expected ${expectedSerial}`)
    }
    validateApplianceLicenseBundlePayload(payload)
    return payload
}

/** Picks the pruned family map for a bundle, dropping empty entries. */
export function normalizeBundleLicenses(licenses: ApplianceLicenseBundleLicenses): ApplianceLicenseBundleLicenses {
    const result: ApplianceLicenseBundleLicenses = {}
    for (const family of APPLIANCE_LICENSE_FAMILIES) {
        const entitlement = licenses[family]
        if (entitlement) {
            result[family] = entitlement
        }
    }
    return result
}

/** Builds the stored/reported bundle status from a verified payload and the token it came from. */
export function applianceLicenseStatusFromBundle(
    payload: ApplianceLicenseBundlePayload,
    token: string
): ApplianceLicenseStatus {
    return {
        version: payload.version,
        issuedAt: payload.issuedAt,
        token,
        licenses: normalizeBundleLicenses(payload.licenses),
    }
}

/**
 * Resolves precedence between two bundle statuses by version — the higher version always wins, with
 * ties going to `incoming`. Either side may be absent. This is the single reconciliation rule shared
 * by every consumer (cloud registration, cloud push, appliance file writes) so a stale bundle can
 * never overwrite a newer one regardless of which path delivered it.
 */
export function pickHigherVersionedBundle(
    existing: ApplianceLicenseStatus | null | undefined,
    incoming: ApplianceLicenseStatus | null | undefined
): ApplianceLicenseStatus | null {
    if (!incoming) return existing ?? null
    if (!existing) return incoming
    return incoming.version >= existing.version ? incoming : existing
}
